import React, { useEffect, useMemo, useState } from "react"
import { Radio, Input, InputNumber, Select, DatePicker, Button, Table, Spin, message } from "antd"
import type { TableColumnsType } from "antd"
import dayjs from "dayjs"
import type {
    EmploymentInfo,
    ProjectOpportunity,
    ReviewWorktime,
    UnsignedReviewDetail,
    UnsignedReviewFormValue,
} from "../types"
import { OPPORTUNITY_STATUS_OPTIONS, UNSIGNED_REASONS } from "../constants"
import { validateUnsignedForm } from "../validate"
import {
    getProjectGrossProfitReviewWorktimesApi,
    saveProjectGrossProfitReviewDraftApi,
    submitProjectGrossProfitReviewApi,
} from "@/services/apis/project"
import ProjectSearchSelect from "./ProjectSearchSelect"
import EmploymentInfoSearchSelect from "./EmploymentInfoSearchSelect"
import { FieldLabel, FormFooter, PageHeader, RestoreBanner, SectionCard, formatHours, formatMoney } from "./shared"

const initValue = (): UnsignedReviewFormValue => ({
    reasonType: "",
    correctProjectId: undefined,
    dataErrorWorktimes: [],
    dataErrorDesc: "",
    relatedProjectId: undefined,
    expectedSignMonth: undefined,
    expectedSignAmount: undefined,
    opportunityStatus: undefined,
    otherDesc: "",
    attachments: [],
})

/**
 * 表单初始值：存在当前复盘自己的保存内容且 kind 匹配时，以保存值覆盖空值（回填）；
 * 无保存内容、kind 不匹配或 payload 为空时退回空值（spec 4.8）。
 */
const buildInitialValue = (detail: UnsignedReviewDetail): UnsignedReviewFormValue => {
    const empty = initValue()
    const saved = detail.savedSubmission
    if (!saved || saved.kind !== "unsigned" || !saved.payload) return empty
    const payload = saved.payload as Partial<UnsignedReviewFormValue>
    return {
        ...empty,
        ...payload,
        dataErrorWorktimes: Array.isArray(payload.dataErrorWorktimes) ? payload.dataErrorWorktimes : [],
        attachments: Array.isArray(payload.attachments) ? payload.attachments : [],
    }
}

/** 保存内容中手动添加的员工（source manual / id 为 0）需还原到 addedEmployees，避免回填后丢失 */
const extractAddedEmployees = (value: UnsignedReviewFormValue): ReviewWorktime[] =>
    value.dataErrorWorktimes.filter((w) => w.source === "manual" || w.id === 0)

/** 判断详情是否命中当前表单类型的保存内容（决定是否展示回填提示条） */
const hasSavedSubmission = (detail: UnsignedReviewDetail): boolean =>
    !!detail.savedSubmission && detail.savedSubmission.kind === "unsigned" && !!detail.savedSubmission.payload

const toOpportunityAmount = (raw: string): number | undefined => {
    const cleaned = raw.replace(/,/g, "").trim()
    if (!cleaned) return undefined
    const parsed = Number(cleaned)
    return Number.isFinite(parsed) ? parsed : undefined
}

const toOpportunityMonth = (raw: string): string | undefined => {
    if (!raw) return undefined
    const parsed = dayjs(raw)
    if (parsed.isValid()) return parsed.format("YYYY-MM")
    const matched = /^(\d{4}-\d{2})/.exec(raw)
    return matched?.[1]
}

const buildWillSignPatchFromOpportunity = (
    opportunity: ProjectOpportunity
): Pick<UnsignedReviewFormValue, "expectedSignMonth" | "expectedSignAmount" | "opportunityStatus"> => ({
    expectedSignMonth: toOpportunityMonth(opportunity.contractDate),
    expectedSignAmount: toOpportunityAmount(opportunity.contractAmount),
    opportunityStatus: opportunity.opportunityStatusName || opportunity.opportunityStatus || undefined,
})

const UnsignedReviewForm: React.FC<{
    detail: UnsignedReviewDetail
    onBack: () => void
    /** 提交成功回调：容器据此把列表项状态就地置为 submitted（spec 5.3） */
    onSubmitted?: (id: string) => void
}> = ({ detail, onBack, onSubmitted }) => {
    // 初始值：命中保存内容时回填（spec 4.8）
    const initialValue = useMemo(() => buildInitialValue(detail), [detail])
    const [value, setValue] = useState<UnsignedReviewFormValue>(initialValue)
    const [submitting, setSubmitting] = useState(false)
    const [savingDraft, setSavingDraft] = useState(false)
    const restored = hasSavedSubmission(detail)
    // 员工数据填错成员工时列表：懒加载，成功后缓存；失败展示错误态与「重试」（与已签约 A1 一致）
    const [worktimes, setWorktimes] = useState<ReviewWorktime[]>([])
    const [worktimesLoading, setWorktimesLoading] = useState(false)
    const [worktimesLoaded, setWorktimesLoaded] = useState(false)
    const [worktimesError, setWorktimesError] = useState(false)
    // 员工数据填错手动添加的员工（通过「添加员工」从用工信息搜索结果补充，无成员工时记录 ID）
    // 回填时按保存内容还原，避免再次打开表单后手动添加的员工丢失
    const [addedEmployees, setAddedEmployees] = useState<ReviewWorktime[]>(() =>
        extractAddedEmployees(initialValue)
    )
    const [showAddEmployee, setShowAddEmployee] = useState(false)
    const [selectedOpportunity, setSelectedOpportunity] = useState<string>()
    const [autoFilledOpportunity, setAutoFilledOpportunity] = useState(false)

    const patch = (p: Partial<UnsignedReviewFormValue>) => setValue((v) => ({ ...v, ...p }))

    const opportunityOptions = useMemo<
        { value: string; label: string; opportunity: ProjectOpportunity }[]
    >(() => {
        const options: { value: string; label: string; opportunity: ProjectOpportunity }[] = []
        const seen = new Set<string>()
        const append = (list: ProjectOpportunity[] | undefined, source: "project_item" | "project_code") => {
            if (!Array.isArray(list)) return
            const sourceLabel = source === "project_item" ? "项目编号" : "工程编号"
            list.forEach((item, index) => {
                const uniq = [
                    item.opportunityCode,
                    item.opportunityName,
                    item.projectItemCode,
                    item.projectCode,
                    item.contractDate,
                    item.contractAmount,
                ].join("|")
                if (seen.has(uniq)) return
                seen.add(uniq)
                const value = `${source}:${item.opportunityCode || item.opportunityName || "unknown"}:${index}`
                const title = item.opportunityName || item.opportunityCode || `商机${index + 1}`
                const status = item.opportunityStatusName || item.opportunityStatus
                // 展示真实的项目编号 / 工程编号，便于区分同一项目下的多条商机
                const sourceCode =
                    source === "project_item" ? item.projectItemCode : item.projectCode
                const sourceText = sourceCode ? `${sourceLabel} ${sourceCode}` : sourceLabel
                const label = status
                    ? `${title} / 状态 ${status}（${sourceText}）`
                    : `${title}（${sourceText}）`
                options.push({ value, label, opportunity: item })
            })
        }
        append(detail.projectOpportunities, "project_item")
        append(detail.projectOpportunitiesByProjectCode, "project_code")
        return options
    }, [detail.projectOpportunities, detail.projectOpportunitiesByProjectCode])

    const opportunityMap = useMemo(
        () => new Map(opportunityOptions.map((item) => [item.value, item.opportunity])),
        [opportunityOptions]
    )

    const opportunityStatusOptions = useMemo(() => {
        const statuses = new Set(OPPORTUNITY_STATUS_OPTIONS)
        opportunityOptions.forEach((item) => {
            const status = item.opportunity.opportunityStatusName || item.opportunity.opportunityStatus
            if (status) statuses.add(status)
        })
        return Array.from(statuses).map((status) => ({ value: status, label: status }))
    }, [opportunityOptions])

    const willSignFieldsEmpty =
        !value.expectedSignMonth &&
        (value.expectedSignAmount === undefined || value.expectedSignAmount === null) &&
        !value.opportunityStatus

    // 保存内容读取失败：按空表单处理并提示，仍可正常填报（spec 4.8）
    useEffect(() => {
        if (detail.savedSubmissionError) message.warning("暂存内容加载失败")
    }, [detail.savedSubmissionError])

    // 首次进入「会签约」并且后端 projectOpportunities 有值时，默认套用第一条商机。
    useEffect(() => {
        if (value.reasonType !== "will_sign" || autoFilledOpportunity) return
        if (!Array.isArray(detail.projectOpportunities) || detail.projectOpportunities.length === 0) {
            setAutoFilledOpportunity(true)
            return
        }
        if (!willSignFieldsEmpty) {
            setAutoFilledOpportunity(true)
            return
        }
        const first = detail.projectOpportunities[0]
        const firstOption = opportunityOptions.find(
            (item) => item.opportunity.opportunityCode === first.opportunityCode
        )
        setValue((prev) => ({ ...prev, ...buildWillSignPatchFromOpportunity(first) }))
        if (firstOption) setSelectedOpportunity(firstOption.value)
        setAutoFilledOpportunity(true)
    }, [
        autoFilledOpportunity,
        detail.projectOpportunities,
        opportunityOptions,
        value.reasonType,
        willSignFieldsEmpty,
    ])

    // 选中「员工数据填错」后懒加载成员工时列表；回填命中 data_error 时同样触发
    useEffect(() => {
        if (value.reasonType === "data_error" && !worktimesLoaded && !worktimesError && !worktimesLoading) {
            void loadWorktimes()
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value.reasonType])

    // 工时列表 = 项目成员工时记录 + 手动添加的员工（按工号去重）；
    // 保存内容中已勾选、但接口与手动列表均不含的记录仍保留展示（不静默丢失）
    const mergedWorktimes = useMemo(() => {
        const base = [
            ...worktimes,
            ...addedEmployees.filter((e) => !worktimes.some((w) => w.clerkCode === e.clerkCode)),
        ]
        const orphans = value.dataErrorWorktimes.filter(
            (w) => !base.some((b) => b.clerkCode === w.clerkCode)
        )
        return [...base, ...orphans]
    }, [worktimes, addedEmployees, value.dataErrorWorktimes])

    const loadWorktimes = async (force = false) => {
        if (worktimesLoading) return
        if (!force && worktimesLoaded) return
        setWorktimesLoading(true)
        setWorktimesError(false)
        try {
            const list = await getProjectGrossProfitReviewWorktimesApi(detail.id)
            setWorktimes(list)
            setWorktimesLoaded(true)
        } catch {
            setWorktimesError(true)
        } finally {
            setWorktimesLoading(false)
        }
    }

    // 「添加员工」：从用工信息搜索结果追加到工时列表并自动勾选；同一员工不允许重复添加
    const handleAddEmployee = (info: EmploymentInfo) => {
        if (mergedWorktimes.some((w) => w.clerkCode === info.clerkCode)) {
            message.warning("该员工已在列表中")
            return
        }
        const added: ReviewWorktime = {
            source: "manual",
            id: 0,
            clerkCode: info.clerkCode,
            name: info.name,
            majorName: info.majorName,
            companyName: info.companyName,
            departmentName: "",
            totalHours: 0,
            workday: 0,
        }
        setAddedEmployees((list) => [...list, added])
        setValue((v) => ({ ...v, dataErrorWorktimes: [...v.dataErrorWorktimes, added] }))
    }

    const handleOpportunityChange = (opportunityValue?: string) => {
        setSelectedOpportunity(opportunityValue)
        if (!opportunityValue) return
        const selected = opportunityMap.get(opportunityValue)
        if (!selected) return
        patch(buildWillSignPatchFromOpportunity(selected))
        setAutoFilledOpportunity(true)
    }

    // 勾选/取消勾选填错人员（保留已填写的修正后的工日数）
    const handleWorktimeSelectionChange = (keys: React.Key[]) => {
        setValue((v) => ({
            ...v,
            dataErrorWorktimes: keys
                .map((k) => {
                    const clerkCode = String(k)
                    const prev = v.dataErrorWorktimes.find((w) => w.clerkCode === clerkCode)
                    if (prev) return prev
                    const row = mergedWorktimes.find((w) => w.clerkCode === clerkCode)
                    return row ? { ...row } : null
                })
                .filter((w): w is ReviewWorktime => w !== null),
        }))
    }

    // 编辑修正后的工日数：仅允许大于等于 0 的数字，非法值不保存
    const handleCorrectedHoursChange = (clerkCode: string, v: number | null) => {
        if (v === null) {
            setValue((prev) => ({
                ...prev,
                dataErrorWorktimes: prev.dataErrorWorktimes.map((w) =>
                    w.clerkCode === clerkCode ? { ...w, correctedTotalHours: undefined } : w
                ),
            }))
            return
        }
        if (isNaN(v) || v < 0) {
            message.warning("修正后的工日数仅支持大于等于 0 的数字")
            return
        }
        setValue((prev) => ({
            ...prev,
            dataErrorWorktimes: prev.dataErrorWorktimes.map((w) =>
                w.clerkCode === clerkCode ? { ...w, correctedTotalHours: v } : w
            ),
        }))
    }

    const worktimeColumns: TableColumnsType<ReviewWorktime> = [
        { title: "工号", dataIndex: "clerkCode", key: "clerkCode", width: 100, render: (v: string) => v || "-" },
        { title: "姓名", dataIndex: "name", key: "name", width: 100, render: (v: string) => v || "-" },
        { title: "专业", dataIndex: "majorName", key: "majorName", width: 120, render: (v: string) => v || "-" },
        { title: "公司", dataIndex: "companyName", key: "companyName", width: 140, render: (v: string) => v || "-" },
        // 未签约展示该项目下全部员工（不过滤部门），带出部门名称
        {
            title: "部门",
            dataIndex: "departmentName",
            key: "departmentName",
            width: 140,
            render: (v: string) => v || "-",
        },
        {
            title: "工日数",
            dataIndex: "workday",
            key: "workday",
            width: 110,
            align: "right",
            render: (v: number) => formatHours(v),
        },
        {
            title: "类型",
            dataIndex: "source",
            key: "source",
            width: 100,
            // 只读展示来源：系统汇总 / 手动添加，不允许手工修改
            render: (v: ReviewWorktime["source"]) => (v === "manual" ? "手动添加" : "系统"),
        },
        {
            title: "修正后的工日数",
            key: "correctedTotalHours",
            width: 150,
            align: "right",
            render: (_, row) => {
                const selected = value.dataErrorWorktimes.find((w) => w.clerkCode === row.clerkCode)
                return (
                    <InputNumber
                        size="small"
                        precision={1}
                        placeholder="选填"
                        disabled={!selected}
                        value={selected?.correctedTotalHours ?? null}
                        onChange={(v) => handleCorrectedHoursChange(row.clerkCode, v)}
                    />
                )
            },
        },
    ]

    /** 基础信息项（下划线取值样式） */
    const infoItems: { label: string; value: React.ReactNode; negative?: boolean }[] = [
        { label: "项目名称（未签约）", value: detail.projectName },
        { label: "项目编号", value: detail.projectNo },
        { label: "所属所 / 管理所长", value: `${detail.department} / ${detail.manager}` },
        { label: "复盘发送月份", value: detail.reviewMonth },
        { label: "首次填报月份", value: detail.firstReportMonth },
        { label: "已投入月数", value: `${detail.investedMonths} 个月` },
        // 人力投入（万元）：并入基础信息项，负数标红
        { label: "本月投入人力成本（万元）", value: formatMoney(detail.monthLaborCost), negative: detail.monthLaborCost < 0 },
        { label: "当年投入人力成本（万元）", value: formatMoney(detail.yearLaborCost), negative: detail.yearLaborCost < 0 },
        { label: "累计投入人力成本（万元）", value: formatMoney(detail.totalLaborCost), negative: detail.totalLaborCost < 0 },
    ]

    const validate = (): string | null =>
        validateUnsignedForm(value, {
            loaded: worktimesLoaded,
            error: worktimesError,
            count: worktimes.length,
        })

    const submit = async () => {
        const err = validate()
        if (err) {
            message.warning(err)
            return
        }
        setSubmitting(true)
        try {
            await submitProjectGrossProfitReviewApi(detail.id, { kind: "unsigned", unsigned: value })
            message.success("提交成功")
            onSubmitted?.(detail.id)
            onBack()
        } catch (e) {
            message.error(typeof e === "string" ? e : "提交失败")
        } finally {
            setSubmitting(false)
        }
    }

    const saveDraft = async () => {
        setSavingDraft(true)
        try {
            await saveProjectGrossProfitReviewDraftApi(detail.id, { kind: "unsigned", unsigned: value })
            message.success("已暂存")
        } catch (e) {
            message.error(typeof e === "string" ? e : "暂存失败")
        } finally {
            setSavingDraft(false)
        }
    }

    return (
        <div className="min-h-full flex flex-col">
            <PageHeader
                title="未签约项目人力投入复盘"
                subtitle={`${detail.projectName}（${detail.projectNo}）`}
                onBack={onBack}
            />

            <div className="flex-1 p-5 space-y-4">
                {restored && detail.savedSubmission?.status === "draft" && (
                    <RestoreBanner
                        status={detail.savedSubmission.status}
                        submittedAt={detail.savedSubmission.submittedAt}
                    />
                )}

                {/* 项目基本信息与人力投入 */}
                <section className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                    {/* 深色标题栏 */}
                    <header className="bg-[#1c4d38] px-5 py-3">
                        <h3 className="text-[15px] leading-6 font-semibold text-white">项目基本信息与人力投入</h3>
                    </header>
                    <div className="p-5">
                        {/* 基础信息：标签 + 下划线取值 */}
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-x-8 gap-y-5">
                            {infoItems.map((it) => (
                                <div key={it.label} className="min-w-0">
                                    <div className="text-xs text-gray-400 mb-1.5">{it.label}</div>
                                    <div
                                        className={`text-sm font-medium break-words border-b border-gray-300 pb-1.5 ${it.negative ? "text-red-500" : "text-gray-800"
                                            }`}
                                    >
                                        {it.value}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                {/* 原因类型 */}
                <SectionCard title="项目情况" required desc="单选，选定后填写对应必填项">
                    <Radio.Group
                        value={value.reasonType}
                        onChange={(e) => patch({ reasonType: e.target.value })}
                        className="w-full"
                    >
                        <div className="space-y-3">
                            {UNSIGNED_REASONS.map((r) => {
                                const active = value.reasonType === r.value
                                return (
                                    <div
                                        key={r.value}
                                        onClick={() => patch({ reasonType: r.value })}
                                        className={`rounded-lg border p-4 cursor-pointer transition-colors ${active ? "border-dark-bg bg-dark-bg/5" : "border-gray-200"
                                            }`}
                                    >
                                        <div className="flex items-start gap-3">
                                            <Radio value={r.value} className="mt-0.5" />
                                            <div>
                                                <div className="text-sm font-medium text-gray-800">{r.label}</div>
                                                <div className="text-xs text-gray-400 mt-0.5">{r.desc}</div>
                                            </div>
                                        </div>

                                        {active && r.value === "wrong_initiation" && (
                                            <div className="mt-3 pl-7">
                                                <FieldLabel required>选正确项目</FieldLabel>
                                                <ProjectSearchSelect
                                                    className="w-full max-w-xl"
                                                    placeholder="搜索并选择实际签约项目"
                                                    value={value.correctProjectId}
                                                    onChange={(v) => patch({ correctProjectId: v })}
                                                />
                                            </div>
                                        )}

                                        {active && r.value === "data_error" && (
                                            <div className="mt-3 pl-7 space-y-3">
                                                {/* 添加员工：从用工信息搜索结果补充填错人员（与已签约 A1 一致） */}
                                                <div className="space-y-2">
                                                    <Button size="small" onClick={() => setShowAddEmployee((s) => !s)}>
                                                        添加员工
                                                    </Button>
                                                    {showAddEmployee && (
                                                        <EmploymentInfoSearchSelect
                                                            className="w-full"
                                                            onSelect={handleAddEmployee}
                                                        />
                                                    )}
                                                </div>
                                                {worktimesLoading ? (
                                                    <div className="py-6 text-center">
                                                        <Spin size="small" />
                                                    </div>
                                                ) : worktimesError ? (
                                                    <div className="flex items-center gap-3 rounded-md bg-red-50 px-3 py-2 text-sm text-red-500">
                                                        成员工时列表加载失败
                                                        <Button size="small" onClick={() => void loadWorktimes(true)}>
                                                            重试
                                                        </Button>
                                                    </div>
                                                ) : mergedWorktimes.length === 0 ? (
                                                    <div className="py-6 text-center text-sm text-gray-400">
                                                        该项目暂无成员工时记录
                                                    </div>
                                                ) : (
                                                    <Table<ReviewWorktime>
                                                        size="small"
                                                        rowKey="clerkCode"
                                                        columns={worktimeColumns}
                                                        dataSource={mergedWorktimes}
                                                        pagination={false}
                                                        scroll={{ x: 920 }}
                                                        rowSelection={{
                                                            columnTitle: "勾选",
                                                            selectedRowKeys: value.dataErrorWorktimes.map((w) => w.clerkCode),
                                                            onChange: handleWorktimeSelectionChange,
                                                        }}
                                                    />
                                                )}
                                                <div>
                                                    <FieldLabel required>具体描述</FieldLabel>
                                                    <Input.TextArea
                                                        rows={3}
                                                        placeholder="错填人员，备注情况（事实 + 数据辅助）"
                                                        value={value.dataErrorDesc}
                                                        onChange={(e) => patch({ dataErrorDesc: e.target.value })}
                                                    />
                                                </div>
                                                <div>
                                                    <FieldLabel>可能影响的关联项目（选填）</FieldLabel>
                                                    <ProjectSearchSelect
                                                        className="w-full max-w-xl"
                                                        placeholder="搜索并选择可能受影响的关联项目"
                                                        allowClear
                                                        value={value.relatedProjectId}
                                                        onChange={(v) => patch({ relatedProjectId: v })}
                                                    />
                                                </div>
                                            </div>
                                        )}

                                        {active && r.value === "will_sign" && (
                                            <div className="mt-3 pl-7">
                                                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 max-w-3xl">
                                                    <div className="sm:col-span-3">
                                                        <FieldLabel>关联商机</FieldLabel>
                                                        <Select
                                                            className="w-full"
                                                            placeholder="请选择商机（自动填充下方字段）"
                                                            allowClear
                                                            value={selectedOpportunity}
                                                            options={opportunityOptions}
                                                            disabled={opportunityOptions.length === 0}
                                                            onChange={(v) => handleOpportunityChange(v)}
                                                        />
                                                    </div>
                                                    <div>
                                                        <FieldLabel required>预计签约时间</FieldLabel>
                                                        <DatePicker
                                                            className="w-full"
                                                            picker="month"
                                                            placeholder="年-月"
                                                            value={
                                                                value.expectedSignMonth
                                                                    ? dayjs(value.expectedSignMonth, "YYYY-MM")
                                                                    : null
                                                            }
                                                            onChange={(d) =>
                                                                patch({
                                                                    expectedSignMonth: d ? d.format("YYYY-MM") : undefined,
                                                                })
                                                            }
                                                        />
                                                    </div>
                                                    <div>
                                                        <FieldLabel required>预计签约金额</FieldLabel>
                                                        <InputNumber
                                                            className="w-full"
                                                            min={0}
                                                            placeholder="请输入"
                                                            addonAfter="万元"
                                                            value={value.expectedSignAmount}
                                                            onChange={(v) =>
                                                                patch({ expectedSignAmount: v ?? undefined })
                                                            }
                                                        />
                                                    </div>
                                                    <div>
                                                        <FieldLabel>商机状态</FieldLabel>
                                                        <Select
                                                            className="w-full"
                                                            placeholder="请选择"
                                                            allowClear
                                                            value={value.opportunityStatus}
                                                            options={opportunityStatusOptions}
                                                            onChange={(v) => patch({ opportunityStatus: v })}
                                                        />
                                                    </div>
                                                </div>
                                                <p className="mt-2 text-xs text-amber-600">
                                                    机制：逾期未签约将重新触发复盘提醒。
                                                </p>
                                            </div>
                                        )}

                                        {active && r.value === "other" && (
                                            <div className="mt-3 pl-7">
                                                <FieldLabel required>具体描述</FieldLabel>
                                                <Input.TextArea
                                                    rows={3}
                                                    placeholder="出了什么问题 - 事实 + 数据辅助（必填，不填视为未达成项）"
                                                    value={value.otherDesc}
                                                    onChange={(e) => patch({ otherDesc: e.target.value })}
                                                />
                                            </div>
                                        )}
                                    </div>
                                )
                            })}
                        </div>
                    </Radio.Group>
                </SectionCard>
            </div>

            <FormFooter
                submitter={detail.manager}
                date={dayjs().format("YYYY-MM-DD")}
                submitting={submitting}
                savingDraft={savingDraft}
                onSaveDraft={saveDraft}
                onSubmit={submit}
            />
        </div>
    )
}

export default UnsignedReviewForm
